// Pool administration: settings, prices and limits, the vehicles with priority and note, swapping
// a car, and the members of a pool that is not the default one.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import type { PoolSummary, PoolVehicleItem } from '@aldorock-fordon/shared/api-types';
import { api, ApiError } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { CheckboxField, ErrorSummary, SelectField, TextArea, TextField } from '../ui/Fields.tsx';
import { Table } from '../ui/Table.tsx';
import { plate } from '../format.ts';

type Member = { id: string; userId: string; name: string; department: string | null };
type User = { id: string; name: string; department: string | null };

type Draft = {
  name: string;
  garage: string;
  address: string;
  description: string;
  adminNote: string;
  isDefault: boolean;
  keyMode: 'skap' | 'ingen' | 'manuell';
  keyWindowMinutes: string;
  start: string;
  perHour: string;
  perDay: string;
  perKm: string;
  maxHours: string;
  maxDaysAhead: string;
};

const toDraft = (p: PoolSummary): Draft => ({
  name: p.name,
  garage: p.garage ?? '',
  address: p.address ?? '',
  description: p.description ?? '',
  adminNote: p.adminNote ?? '',
  isDefault: p.isDefault,
  keyMode: p.keyMode,
  keyWindowMinutes: String(p.keyWindowMinutes),
  start: String(p.priceModel.start),
  perHour: String(p.priceModel.perHour),
  perDay: String(p.priceModel.perDay),
  perKm: String(p.priceModel.perKm),
  maxHours: p.limits.maxHours ? String(p.limits.maxHours) : '',
  maxDaysAhead: p.limits.maxDaysAhead ? String(p.limits.maxDaysAhead) : '',
});

const n = (s: string) => (s.trim() === '' ? 0 : Number(s));
const nOrNull = (s: string) => (s.trim() === '' ? null : Number(s));

function PoolForm({ pool, onSaved }: { pool: PoolSummary; onSaved: () => void }) {
  const [d, setD] = useState<Draft>(() => toDraft(pool));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof Draft) => (v: string | boolean) => setD({ ...d, [k]: v });
  const save = useMutation({
    mutationFn: () =>
      api(`/pools/${pool.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: d.name,
          garage: d.garage || null,
          address: d.address || null,
          description: d.description || null,
          adminNote: d.adminNote || null,
          isDefault: d.isDefault,
          keyMode: d.keyMode,
          keyWindowMinutes: n(d.keyWindowMinutes),
          priceModel: {
            start: n(d.start),
            perHour: n(d.perHour),
            perDay: n(d.perDay),
            perKm: n(d.perKm),
          },
          limits: { maxHours: nOrNull(d.maxHours), maxDaysAhead: nOrNull(d.maxDaysAhead) },
        }),
      }),
    onSuccess: onSaved,
    onError: (err) => {
      if (err instanceof ApiError && err.errors.length)
        setErrors(Object.fromEntries(err.errors.map((e) => [e.path, e.message])));
    },
  });
  return (
    <form
      className="form-block"
      aria-labelledby="pool-form"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <h2 className="form-heading" id="pool-form">
        {sv.poolAdmin.edit}
      </h2>
      <ErrorSummary errors={Object.entries(errors).map(([k, m]) => ({ id: k, message: m }))} />
      <TextField
        id="name"
        label={sv.poolAdmin.name}
        value={d.name}
        required
        error={errors['name']}
        onChange={set('name')}
      />
      <TextField
        id="garage"
        label={sv.poolAdmin.garage}
        value={d.garage}
        onChange={set('garage')}
      />
      <TextField
        id="address"
        label={sv.poolAdmin.address}
        value={d.address}
        onChange={set('address')}
      />
      <TextArea
        id="description"
        label={sv.poolAdmin.description}
        value={d.description}
        rows={3}
        onChange={set('description')}
      />
      <TextArea
        id="adminNote"
        label={sv.poolAdmin.adminNote}
        value={d.adminNote}
        rows={2}
        onChange={set('adminNote')}
      />
      <CheckboxField
        id="isDefault"
        label={sv.poolAdmin.isDefault}
        checked={d.isDefault}
        onChange={set('isDefault')}
      />
      <SelectField
        id="keyMode"
        label={sv.poolAdmin.keyMode}
        value={d.keyMode}
        options={Object.entries(sv.pools.keyMode).map(([value, label]) => ({ value, label }))}
        onChange={(v) => setD({ ...d, keyMode: v as Draft['keyMode'] })}
      />
      <TextField
        id="keyWindowMinutes"
        label={sv.poolAdmin.keyWindow}
        type="number"
        value={d.keyWindowMinutes}
        onChange={set('keyWindowMinutes')}
      />
      <h3 className="form-heading">{sv.pools.price}</h3>
      <TextField
        id="start"
        label={sv.poolAdmin.priceStart}
        type="number"
        value={d.start}
        onChange={set('start')}
      />
      <TextField
        id="perHour"
        label={sv.poolAdmin.pricePerHour}
        type="number"
        value={d.perHour}
        onChange={set('perHour')}
      />
      <TextField
        id="perDay"
        label={sv.poolAdmin.pricePerDay}
        type="number"
        value={d.perDay}
        onChange={set('perDay')}
      />
      <TextField
        id="perKm"
        label={sv.poolAdmin.pricePerKm}
        type="number"
        value={d.perKm}
        onChange={set('perKm')}
      />
      <h3 className="form-heading">
        {sv.booking.errors.noPool.replace('Välj en bilpool.', '') || sv.poolAdmin.limitsHelp}
      </h3>
      <TextField
        id="maxHours"
        label={sv.poolAdmin.maxHours}
        help={sv.poolAdmin.limitsHelp}
        type="number"
        value={d.maxHours}
        onChange={set('maxHours')}
      />
      <TextField
        id="maxDaysAhead"
        label={sv.poolAdmin.maxDaysAhead}
        help={sv.poolAdmin.limitsHelp}
        type="number"
        value={d.maxDaysAhead}
        onChange={set('maxDaysAhead')}
      />
      <div className="form-actions">
        <button
          className="btn btn-primary"
          type="submit"
          disabled={save.isPending}
          aria-busy={save.isPending}
        >
          {sv.poolAdmin.save}
        </button>
      </div>
    </form>
  );
}

function VehicleRow({ poolId, v }: { poolId: string; v: PoolVehicleItem }) {
  const qc = useQueryClient();
  const [priority, setPriority] = useState(String(v.priority));
  const [note, setNote] = useState(v.note ?? '');
  const [active, setActive] = useState(v.active);
  const [swap, setSwap] = useState('');
  const [swapping, setSwapping] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const refresh = () => qc.invalidateQueries({ queryKey: ['pool-vehicles', poolId] });
  const save = useMutation({
    mutationFn: () =>
      api(`/pools/${poolId}/vehicles/${v.vehicleId}`, {
        method: 'PUT',
        body: JSON.stringify({ priority: Number(priority) || 1, note: note || null, active }),
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api(`/pools/${poolId}/vehicles/${v.vehicleId}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (err) =>
      setError(err instanceof ApiError && err.errors[0] ? err.errors[0].message : sv.errors.server),
  });
  const doSwap = useMutation({
    mutationFn: () =>
      api(`/pools/${poolId}/vehicles/${v.vehicleId}/swap`, {
        method: 'POST',
        body: JSON.stringify({ plate: swap }),
      }),
    onSuccess: async () => {
      setSwapping(false);
      await refresh();
    },
    onError: (err) =>
      setError(err instanceof ApiError && err.errors[0] ? err.errors[0].message : sv.errors.server),
  });
  const label = `${[v.make, v.model].filter(Boolean).join(' ')} ${plate(v.registrationNumber)}`;
  return (
    <tr>
      <td>
        <Link to={`/fordon/${v.vehicleId}`}>{label}</Link>
        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}
        {swapping ? (
          <form
            className="label-row row"
            onSubmit={(e) => {
              e.preventDefault();
              doSwap.mutate();
            }}
          >
            <TextField
              id={`swap-${v.vehicleId}`}
              label={sv.poolAdmin.swapTitle(plate(v.registrationNumber))}
              help={sv.poolAdmin.swapHelp}
              value={swap}
              onChange={setSwap}
            />
            <button className="btn btn-primary" type="submit" disabled={doSwap.isPending}>
              {sv.poolAdmin.swapButton}
            </button>
            <button className="btn" type="button" onClick={() => setSwapping(false)}>
              {sv.vehicleCard.cancelEdit}
            </button>
          </form>
        ) : null}
      </td>
      <td>
        <label className="visually-hidden" htmlFor={`prio-${v.vehicleId}`}>
          {`${sv.poolAdmin.priority} ${label}`}
        </label>
        <input
          className="input short"
          id={`prio-${v.vehicleId}`}
          type="number"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        />
      </td>
      <td>
        <label className="visually-hidden" htmlFor={`note-${v.vehicleId}`}>
          {`${sv.poolAdmin.note} ${label}`}
        </label>
        <input
          className="input"
          id={`note-${v.vehicleId}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </td>
      <td>
        <label className="choice" htmlFor={`active-${v.vehicleId}`}>
          <input
            type="checkbox"
            id={`active-${v.vehicleId}`}
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          <span className="visually-hidden">{`${sv.poolAdmin.active} ${label}`}</span>
        </label>
      </td>
      <td>
        <span className="actions-inline">
          <button
            className="btn btn-quiet"
            type="button"
            onClick={() => save.mutate()}
            aria-label={`${sv.vehicleCard.save} ${label}`}
          >
            {sv.vehicleCard.save}
          </button>
          <button
            className="btn btn-quiet"
            type="button"
            onClick={() => setSwapping(true)}
            aria-label={`${sv.poolAdmin.swap} ${label}`}
          >
            {sv.poolAdmin.swap}
          </button>
          <button
            className="btn btn-quiet"
            type="button"
            onClick={() => remove.mutate()}
            aria-label={`${sv.poolAdmin.remove} ${label}`}
          >
            {sv.poolAdmin.remove}
          </button>
        </span>
      </td>
    </tr>
  );
}

function Members({ pool }: { pool: PoolSummary }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const members = useQuery({
    queryKey: ['pool-members', pool.id],
    queryFn: () => api<Member[]>(`/pools/${pool.id}/members`),
  });
  const users = useQuery({
    queryKey: ['users', q],
    queryFn: () => api<User[]>(`/users?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
  });
  const add = useMutation({
    mutationFn: (userId: string) =>
      api(`/pools/${pool.id}/members`, { method: 'POST', body: JSON.stringify({ userId }) }),
    onSuccess: async () => {
      setQ('');
      await qc.invalidateQueries({ queryKey: ['pool-members', pool.id] });
    },
  });
  const remove = useMutation({
    mutationFn: (memberId: string) =>
      api(`/pools/${pool.id}/members/${memberId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pool-members', pool.id] }),
  });
  return (
    <section className="block" aria-labelledby="members">
      <h2 className="block-title" id="members">
        {sv.poolAdmin.members}
      </h2>
      {pool.isDefault ? <p className="muted">{sv.poolAdmin.membersDefault}</p> : null}
      <ul className="plain">
        {members.data?.map((m) => (
          <li key={m.id} className="row">
            <span>
              {m.name}
              {m.department ? <span className="muted"> · {m.department}</span> : null}
            </span>
            <button
              className="btn btn-quiet"
              type="button"
              onClick={() => remove.mutate(m.id)}
              aria-label={`${sv.poolAdmin.removeMember} ${m.name}`}
            >
              {sv.poolAdmin.removeMember}
            </button>
          </li>
        ))}
      </ul>
      <div className="form-block">
        <TextField id="member-q" label={sv.poolAdmin.addMember} value={q} onChange={setQ} />
        {users.data?.length ? (
          <ul className="plain driver-hits">
            {users.data.map((u) => (
              <li key={u.id}>
                <button className="link-button" type="button" onClick={() => add.mutate(u.id)}>
                  {u.name}
                  {u.department ? <span className="muted"> · {u.department}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

export function PoolAdminPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const pools = useQuery({ queryKey: ['pools'], queryFn: () => api<PoolSummary[]>('/pools') });
  const pool = pools.data?.find((p) => p.id === id) ?? null;
  const vehicles = useQuery({
    queryKey: ['pool-vehicles', id],
    queryFn: () => api<PoolVehicleItem[]>(`/pools/${id}/vehicles`),
  });
  const h1 = usePage(pool ? pool.name : sv.poolAdmin.title);
  const [message, setMessage] = useState<string | null>(null);
  const [addPlate, setAddPlate] = useState('');
  const [addError, setAddError] = useState<string | undefined>();
  const add = useMutation({
    mutationFn: () =>
      api(`/pools/${id}/vehicles`, { method: 'POST', body: JSON.stringify({ plate: addPlate }) }),
    onSuccess: async () => {
      setAddPlate('');
      setAddError(undefined);
      await qc.invalidateQueries({ queryKey: ['pool-vehicles', id] });
      await qc.invalidateQueries({ queryKey: ['pools'] });
    },
    onError: (err) =>
      setAddError(
        err instanceof ApiError && err.errors[0] ? err.errors[0].message : sv.errors.server,
      ),
  });
  return (
    <>
      <PageHead
        help="poolAdmin"
        crumbs={[{ to: '/bilpooler', label: sv.poolAdmin.crumb }, { label: pool?.name ?? '' }]}
        title={pool ? pool.name : sv.poolAdmin.title}
        titleRef={h1}
      />
      {message ? (
        <p className="notice" data-state="done" role="status">
          {message}
        </p>
      ) : null}
      {pool ? (
        <>
          <PoolForm
            key={pool.id + pool.name}
            pool={pool}
            onSaved={async () => {
              await qc.invalidateQueries({ queryKey: ['pools'] });
              setMessage(sv.poolAdmin.saved);
              window.scrollTo(0, 0);
            }}
          />
          <Table caption={sv.poolAdmin.vehicles} isEmpty={(vehicles.data?.length ?? 0) === 0}>
            <thead>
              <tr>
                <th scope="col">{sv.columns.vehicle}</th>
                <th scope="col">{sv.poolAdmin.priority}</th>
                <th scope="col">{sv.poolAdmin.note}</th>
                <th scope="col">{sv.poolAdmin.active}</th>
                <th scope="col">{sv.columns.action}</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.data?.map((v) => (
                <VehicleRow key={v.vehicleId} poolId={pool.id} v={v} />
              ))}
            </tbody>
          </Table>
          <form
            className="form-block"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate();
            }}
          >
            <TextField
              id="add-plate"
              label={sv.poolAdmin.addVehicleLabel}
              value={addPlate}
              error={addError}
              onChange={setAddPlate}
            />
            <div className="form-actions">
              <button className="btn" type="submit" disabled={add.isPending || !addPlate.trim()}>
                {sv.poolAdmin.addVehicleButton}
              </button>
            </div>
          </form>
          <Members pool={pool} />
        </>
      ) : (
        <p role="status">{sv.app.loading}</p>
      )}
    </>
  );
}
